import { FinancialUnavailable, isCurrentAssessment, type FinancialResponseAvailability } from '../financial-unavailable';
import { DealFinancialEntryLinks } from '../deal-financial-entry-links';
import { LenderHealthScoresPanel } from '../panels/lender-health-scores-panel';
/**
 * Serviceability Dashboard (SPRINT-15 F008)
 *
 * Deal serviceability dashboard with ICR/DSR gauge charts, lender requirement
 * markers, pass/fail status banner, headroom percentages, trend indicators,
 * and lender health score summary.
 *
 * Reads from the consolidated snapshot API (F003) to derive all metrics.
 *
 * States:
 * - Loading: Skeleton gauges + spinner
 * - No data: Configuration prompt with numbered steps
 * - Loaded (PASS): Green-themed banner, green gauge zones
 * - Loaded (FAIL): Red-themed banner, red highlights
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CheckCircle2,
  XCircle,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Shield,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ConsolidatedSnapshot extends FinancialResponseAvailability {
  id: string;
  dealId: string;
  snapshotDate: string;
  periodLabel: string;
  totalEntityRevenue: number | null;
  totalEntityEbitda: number | null;
  totalEliminations: number | null;
  totalGuarantorIncome: number | null;
  totalAdjustments: number | null;
  adjustedEbitda: number | null;
  totalAssets: number | null;
  totalLiabilities: number | null;
  netAssets: number | null;
  totalIncome: number | null;
  totalCommitments: number | null;
  dscr: number | null;
  icr: number | null;
  surplus: number | null;
  passesServiceability: boolean | null;
  entitiesIncluded: number | null;
  guarantorsIncluded: number | null;
  calculationNotes: string | null;
}

export interface ServiceabilityDashboardProps {
  dealId: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Default lender minimum DSCR requirement */
const DEFAULT_LENDER_MIN_DSCR = 1.25;

/** Default lender minimum ICR requirement */
const DEFAULT_LENDER_MIN_ICR = 2.0;

/** Maximum scale for the DSCR gauge */
const DSCR_MAX_SCALE = 3.0;

/** Maximum scale for the ICR gauge */
const ICR_MAX_SCALE = 6.0;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatRatio(value: number | null, unit: string = "x"): string {
  if (value == null) return "\u2014";
  return `${Number(value).toFixed(2)}${unit}`;
}

function formatPercent(value: number | null): string {
  if (value == null) return "\u2014";
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(0)}%`;
}

/**
 * Classify the status of a ratio against its lender requirement.
 * Returns a label and Tailwind class for colour-coding.
 */
function getStatusLabel(
  actual: number | null,
  required: number
): { label: string; className: string } {
  if (actual === null) return { label: "N/A", className: "text-muted-foreground" };
  const ratio = actual / required;
  if (ratio >= 1.5) return { label: "Strong", className: "text-green-600" };
  if (ratio >= 1.1) return { label: "Adequate", className: "text-blue-600" };
  if (ratio >= 1.0) return { label: "Marginal", className: "text-yellow-600" };
  return { label: "Weak", className: "text-red-600" };
}

/**
 * Determine the gauge fill colour based on actual vs required ratio.
 */
function getGaugeColour(actual: number | null, required: number): string {
  if (actual === null) return "bg-muted";
  const ratio = actual / required;
  if (ratio >= 1.5) return "bg-green-500";
  if (ratio >= 1.1) return "bg-blue-500";
  if (ratio >= 1.0) return "bg-yellow-500";
  return "bg-red-500";
}

/**
 * Calculate headroom percentage: ((actual / required) - 1) * 100
 */
function calcHeadroomPercent(actual: number | null, required: number): number | null {
  if (actual === null || required <= 0) return null;
  return ((actual / required) - 1) * 100;
}

// ---------------------------------------------------------------------------
// Sub-component: LinearGauge
// ---------------------------------------------------------------------------

interface GaugeProps {
  label: string;
  actual: number | null;
  required: number;
  maxScale: number;
  unit: string;
  trend?: number | null;
  lenderName?: string;
}

function LinearGauge({
  label: _label,
  actual,
  required,
  maxScale,
  unit,
  trend = null,
  lenderName,
}: GaugeProps) {
  const fillPercent = actual !== null ? Math.min(100, Math.max(0, (actual / maxScale) * 100)) : 0;
  const requiredPercent = Math.min(100, Math.max(0, (required / maxScale) * 100));
  const headroomPercent = calcHeadroomPercent(actual, required);
  const status = getStatusLabel(actual, required);
  const gaugeColour = getGaugeColour(actual, required);

  const trendIcon =
    trend !== null && trend > 0 ? (
      <TrendingUp className="h-3.5 w-3.5 text-green-600" />
    ) : trend !== null && trend < 0 ? (
      <TrendingDown className="h-3.5 w-3.5 text-red-600" />
    ) : (
      <Minus className="h-3.5 w-3.5 text-muted-foreground" />
    );

  const trendLabel =
    trend !== null && trend > 0
      ? "Improving"
      : trend !== null && trend < 0
        ? "Declining"
        : "Stable";

  return (
    <div className="space-y-3">
      {/* Metrics row */}
      <div className="grid grid-cols-3 gap-2 text-sm">
        <div>
          <p className="text-xs text-muted-foreground">Actual</p>
          <p className="font-semibold font-mono text-base">
            {formatRatio(actual, unit)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Required</p>
          <p className="font-medium font-mono">
            {formatRatio(required, unit)}
            {lenderName && (
              <span className="text-xs text-muted-foreground ml-1">
                ({lenderName})
              </span>
            )}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Headroom</p>
          <p
            className={cn(
              "font-medium font-mono",
              headroomPercent !== null && headroomPercent >= 0
                ? "text-green-600"
                : headroomPercent !== null
                  ? "text-red-600"
                  : "text-muted-foreground"
            )}
          >
            {formatPercent(headroomPercent)}
          </p>
        </div>
      </div>

      {/* Status badge */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Status:</span>
        {actual !== null && actual >= required ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
        ) : actual !== null ? (
          <AlertTriangle className="h-3.5 w-3.5 text-red-600" />
        ) : null}
        <span className={cn("text-sm font-semibold uppercase", status.className)}>
          {status.label}
        </span>
      </div>

      {/* Gauge bar */}
      <div className="relative h-5 w-full rounded-full bg-muted overflow-hidden">
        {/* Fill */}
        <div
          className={cn("absolute inset-y-0 left-0 rounded-full transition-all duration-500", gaugeColour)}
          style={{ width: `${fillPercent}%` }}
        />
        {/* Required marker */}
        <div
          className="absolute top-0 h-full w-0.5 bg-foreground/70"
          style={{ left: `${requiredPercent}%` }}
        />
        {/* Required marker triangle */}
        <div
          className="absolute -bottom-2.5 -translate-x-1/2"
          style={{ left: `${requiredPercent}%` }}
        >
          <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[6px] border-b-foreground/70 rotate-180" />
        </div>
      </div>

      {/* Scale labels */}
      <div className="flex justify-between text-[10px] text-muted-foreground px-1">
        <span>0</span>
        <span>{(maxScale / 2).toFixed(1)}{unit}</span>
        <span>{maxScale.toFixed(1)}{unit}</span>
      </div>

      {/* Trend */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
        <span>Trend:</span>
        {trendIcon}
        <span>{trendLabel}</span>
        {trend !== null && (
          <span className="font-mono">
            ({trend >= 0 ? "+" : ""}{trend.toFixed(1)})
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function ServiceabilityDashboard({ dealId }: ServiceabilityDashboardProps) {
  // Fetch consolidated snapshots (most recent first)
  const {
    data: snapshots,
    isLoading,
    isError,
  } = useQuery<ConsolidatedSnapshot[]>({
    queryKey: [`/api/financial/deals/${dealId}/consolidated-snapshots`],
    enabled: !!dealId,
  });

  // Use the most recent snapshot
  const snapshot = useMemo(() => {
    if (!snapshots || snapshots.length === 0) return null;
    return snapshots[0];
  }, [snapshots]);

  // Derive a simple trend by comparing two most recent snapshots (if available)
  const dscrTrend = useMemo(() => {
    if (!snapshots || snapshots.length < 2 || !isCurrentAssessment(snapshots[0]) || !isCurrentAssessment(snapshots[1])) return null;
    const current = snapshots[0].dscr;
    const previous = snapshots[1].dscr;
    if (current === null || previous === null) return null;
    return parseFloat((current - previous).toFixed(2));
  }, [snapshots]);

  const icrTrend = useMemo(() => {
    if (!snapshots || snapshots.length < 2 || !isCurrentAssessment(snapshots[0]) || !isCurrentAssessment(snapshots[1])) return null;
    const current = snapshots[0].icr;
    const previous = snapshots[1].icr;
    if (current === null || previous === null) return null;
    return parseFloat((current - previous).toFixed(2));
  }, [snapshots]);

  // -------------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div data-testid="serviceability-dashboard" className="space-y-4">
        {/* Status banner skeleton */}
        <Skeleton className="h-16 w-full rounded-lg" />

        {/* Gauge skeletons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-5 w-full rounded-full" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-5 w-full rounded-full" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        </div>

        {/* Health score skeleton */}
        <Card>
          <CardHeader className="pb-3">
            <Skeleton className="h-4 w-36" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // No data state
  // -------------------------------------------------------------------------

  if (isError) {
    return <FinancialUnavailable dealId={dealId} value={{ assessmentStatus: 'failed', historical: snapshot }} action="Refresh the financial data and try again." />;
  }

  if (!snapshot) {
    return (
      <div data-testid="serviceability-dashboard" className="space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-sm font-semibold">
                Serviceability Dashboard
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <AlertTriangle className="h-10 w-10 text-muted-foreground mb-4" />
              <p className="text-sm font-medium text-muted-foreground mb-4">
                Complete the financial analysis configuration to generate
                serviceability assessment
              </p>
              <ol className="text-xs text-muted-foreground space-y-2 text-left max-w-sm">
                <li className="flex items-start gap-2">
                  <Badge variant="outline" className="shrink-0 mt-0.5 h-5 w-5 p-0 justify-center text-[10px]">
                    1
                  </Badge>
                  <span>
                    Add entities and guarantors to the deal analysis configuration
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Badge variant="outline" className="shrink-0 mt-0.5 h-5 w-5 p-0 justify-center text-[10px]">
                    2
                  </Badge>
                  <span>
                    Select financial periods and income/commitment sources
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Badge variant="outline" className="shrink-0 mt-0.5 h-5 w-5 p-0 justify-center text-[10px]">
                    3
                  </Badge>
                  <span>
                    Run the consolidated calculation to generate a snapshot
                  </span>
                </li>
              </ol>
              <DealFinancialEntryLinks dealId={dealId} />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Loaded state
  // -------------------------------------------------------------------------

  if (!isCurrentAssessment(snapshot) || snapshot.passesServiceability == null) {
    return <Card data-testid="serviceability-dashboard"><CardHeader><CardTitle>Serviceability</CardTitle></CardHeader><CardContent>
      <FinancialUnavailable dealId={dealId} value={snapshot} action="Review financial inputs in the financial structure, then recalculate." />
      <LenderHealthScoresPanel dealId={dealId} />
    </CardContent></Card>;
  }
  const passes = snapshot.passesServiceability === true;


  return (
    <div data-testid="serviceability-dashboard" className="space-y-4">
      {/* ================================================================= */}
      {/* SECTION 1: STATUS BANNER                                          */}
      {/* ================================================================= */}
      <Card
        data-testid="status-banner"
        className={cn(
          "border-2",
          passes
            ? "bg-green-50 border-green-300 dark:bg-green-950/30 dark:border-green-800"
            : "bg-red-50 border-red-300 dark:bg-red-950/30 dark:border-red-800"
        )}
      >
        <CardContent className="py-4">
          <div className="flex flex-col items-center justify-center text-center gap-1">
            <div className="flex items-center gap-2">
              {passes ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span
                    data-testid="status-pass"
                    className="text-base font-bold uppercase tracking-wide text-green-700 dark:text-green-400"
                  >
                    Serviceability Status: Passes
                  </span>
                </>
              ) : (
                <>
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span
                    data-testid="status-fail"
                    className="text-base font-bold uppercase tracking-wide text-red-700 dark:text-red-400"
                  >
                    Serviceability Status: Does Not Pass
                  </span>
                </>
              )}
            </div>
            <p
              className={cn(
                "text-xs",
                passes
                  ? "text-green-600 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              )}
            >
              {passes
                ? "All lender requirements met"
                : "One or more lender requirements not met"}
            </p>
            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
              <span>DSCR: {formatRatio(snapshot.dscr, "x")}</span>
              <span>ICR: {formatRatio(snapshot.icr, "x")}</span>
              <span>Surplus: {formatCurrency(snapshot.surplus ?? 0)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ================================================================= */}
      {/* SECTION 2 & 3: ICR + DSR GAUGE CARDS                             */}
      {/* ================================================================= */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* ICR Gauge */}
        <Card data-testid="icr-gauge">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Interest Coverage Ratio (ICR)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LinearGauge
              label="ICR"
              actual={snapshot.icr}
              required={DEFAULT_LENDER_MIN_ICR}
              maxScale={ICR_MAX_SCALE}
              unit="x"
              trend={icrTrend}
              lenderName="Lender min."
            />
          </CardContent>
        </Card>

        {/* DSR / DSCR Gauge */}
        <Card data-testid="dsr-gauge">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Debt Service Coverage Ratio (DSCR)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LinearGauge
              label="DSCR"
              actual={snapshot.dscr}
              required={DEFAULT_LENDER_MIN_DSCR}
              maxScale={DSCR_MAX_SCALE}
              unit="x"
              trend={dscrTrend}
              lenderName="Lender min."
            />
          </CardContent>
        </Card>
      </div>

      {/* ================================================================= */}
      {/* SECTION 4: LENDER HEALTH SCORE                                    */}
      {/* ================================================================= */}
      <LenderHealthScoresPanel dealId={dealId} />
    </div>
  );
}

export default ServiceabilityDashboard;
